import { Box, Button, Grid, Typography } from "@mui/material";
import ABCLogo from "../../assets/images/ABC-Service.jpg";
import MobileNumberEntry from "./MobileNumberEntry/MobileNumberEntry";
import EnterOtp from "../../features/Feature_Login/OtpEntry/OtpEntry";
import {
  useAppDispatch,
  useAppSelector,
} from "../../redux-store/CustomReduxHooks";
import CustomPopup from "../../components/CustomPopup/CustomPopup";
import CommonGifAnimation from "../../components/CustomGifAnimation/CommonGifAnimation";
import { Colors } from "../../styles/constant";
import {
  E_ChannelOptions,
  LoginScreenValues,
  T_AnimationData,
} from "../../globalTypes/GlobalTypes";
import ExclamationMarkGif from "../../assets/gif/Exclamation_Mark.json";
import CsLocalStorageUtils from "../../utils/CsLocalStorageUtils";
import { useNavigate } from "react-router-dom";
import {
  ABCD_SUPERAPP_URL,
  DEVICE_TYPE,
  functionalityCodeForGenerateOtpApi,
  iOSType,
  REGISTER_BIO_METRIC,
} from "../../utils/constants";
import { useEffect, useState, useRef } from "react"; // Added useRef
import { updateLoadingState } from "../Feature_Loading/Loading_Slice";
import { Footer } from "../../components/CustomFooter/CustomFooter";
import "./Feature_Login.scss";
import CustomNetworkImage from "../../components/CustomNetworkImage/CustomNetworkImage";
import {
  defaultEmptyFun,
  getMobileOperatingSystem,
  getModuleNameFromPath,
} from "../../utils/helper";
import {
  resetLoginData,
  setIsViaBioMeticLogin,
  updateLoginData,
  updateScreenTag,
} from "./Login_Slice";
import {
  clearCustomerAndLobDetailsArr,
  saveCsUserId,
} from "../../redux-store/reducers/CsAppSlice";
import { useLazyGenerateOtpQuery } from "./Login_Api";
import { clearError, setError } from "../../redux-store/reducers/CsErrorSlice";
import {
  getCookie,
  BIOMETRIC_DO_IT_LATER_STATUS,
} from "../../utils/CSCookieHandler";
import { useBiometric } from "../../hooks/useBiometric";
import _, { isEmpty } from "lodash";
import useDeviceDetection from "../../hooks/useDeviceDetection";
import LoginWithMPIN from "./LoginWithMPIN/LoginWithMPIN";
import SetupMpn from "../Feature_GeneralAppElements/SetupMpn/SetupMpn";
import useWindowSize from "../../hooks/useWindowSize";
import CustomOnePlatformBox from "../../components/CustomOnePlatformBox/CustomOnePlatformBox";
import { useSaveUIException } from "../../hooks/useSaveUIException";

const FeatureLogin: React.FC = () => {
  const [doItLater, setDoItLater] = useState<boolean>(
    getCookie(BIOMETRIC_DO_IT_LATER_STATUS) === "true",
  );

  const [animationStage, setAnimationStage] = useState(0);
  const loginSlice = useAppSelector((state) => state.LoginSlice);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const csAppSlice = useAppSelector((state) => state.CsAppSlice);
  const secondaryChannelSlice = useAppSelector(
    (state) => state.SecondaryChannelSlice,
  );
  const cmsData = useAppSelector((state) => state.CmsSlice.CmsPageVariableData);
  const csErrorData = useAppSelector((state) => state.CsErrorSlice);
  const cmsLogo =
    cmsData.find((i) => i.attributes.cmsType === "ABC Logo")?.attributes.image
      .data?.attributes.url ?? "";
  const { width } = useWindowSize();
  const [generateOtpApi] = useLazyGenerateOtpQuery();
  const { triggerSaveUIException } = useSaveUIException();
  const initiateBiometricScan = useBiometric("verify");
  const deviceType = useDeviceDetection();
  const [isAskForConsentVisible, setIsAskForConsentVisible] = useState(false);
  const isMobile = deviceType === DEVICE_TYPE.MOBILE;

  const mobileNoFromLocalStorage = CsLocalStorageUtils.get("MOBILE");

  const loginCardRef = useRef<HTMLDivElement>(null);
  const dummyDivRef = useRef<HTMLDivElement>(null);
  const [dummyDivHeight, setDummyDivHeight] = useState<number>(0);

  useEffect(() => {
    if (loginCardRef.current && dummyDivRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        if (loginCardRef.current && dummyDivRef.current) {
          const loginCardHeight = loginCardRef.current.offsetHeight;
          setDummyDivHeight(loginCardHeight);
          dummyDivRef.current.style.height = `${loginCardHeight}px`;
        }
      });

      resizeObserver.observe(loginCardRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [animationStage]);

  // Add scroll prevention ref
  const scrollPreventionApplied = useRef(false);

  const onAskConsentValueChange = (newVal: boolean) => {
    setIsAskForConsentVisible(newVal);
  };

  // Add scroll prevention useEffect
  useEffect(() => {
    const isOtpScreen =
      loginSlice.screenTag === LoginScreenValues.enterOtpScreen;
    const isWelcomeScreen =
      loginSlice.screenTag === LoginScreenValues.enterMobileScreen;
    const isMPinScreen =
      loginSlice.screenTag === LoginScreenValues.LoginWithMPIN;

    const html = document.documentElement;
    const body = document.body;

    if (
      (isOtpScreen || isWelcomeScreen || isMPinScreen) &&
      !scrollPreventionApplied.current &&
      !isEmpty(csAppSlice.NativeDeviceInfo?.deviceName)
    ) {
      // Apply scroll prevention classes
      html.classList.add("otp-no-scroll");
      body.classList.add("otp-no-scroll");

      // Store current scroll position
      const scrollY = window.scrollY;

      // Prevent scroll on touch devices
      const preventDefault = (e: Event) => e.preventDefault();

      // Add touch move listeners for mobile
      document.addEventListener("touchmove", preventDefault, {
        passive: false,
      });
      document.addEventListener("wheel", preventDefault, { passive: false });

      scrollPreventionApplied.current = true;

      // Cleanup function
      return () => {
        html.classList.remove("otp-no-scroll");
        body.classList.remove("otp-no-scroll");
        document.removeEventListener("touchmove", preventDefault);
        document.removeEventListener("wheel", preventDefault);
        scrollPreventionApplied.current = false;

        // Restore scroll position if needed
        window.scrollTo(0, scrollY);
      };
    } else if (!isOtpScreen && scrollPreventionApplied.current) {
      // Remove scroll prevention when not on OTP screen
      html.classList.remove("otp-no-scroll");
      body.classList.remove("otp-no-scroll");
      scrollPreventionApplied.current = false;
    }
  }, [loginSlice.screenTag]);

  useEffect(() => {
    if (width < 900) {
      const timers = [
        setTimeout(() => setAnimationStage(1), 1000),
        setTimeout(() => setAnimationStage(2), 1500),
        setTimeout(() => setAnimationStage(3), 2800),
        setTimeout(() => setAnimationStage(4), 3500),
      ];
      return () => timers.forEach((t) => clearTimeout(t));
    }
  }, [width]);

  useEffect(() => {
    if (!isEmpty(csErrorData.error)) {
      dispatch(clearError());
    }
  }, []);

  const attemptBiometricLogin = (bioInfo: any) => {
    if (!_.isEmpty(bioInfo) && !_.isEmpty(bioInfo?.biometricToken)) {
      initiateBiometricScan(bioInfo?.biometricToken)
        ?.then((result) => {
          dispatch(setIsViaBioMeticLogin(true));
          dispatch(updateLoginData({ key: "isUserLoggedIn", value: true }));
          dispatch(
            updateLoginData({ key: "mobilenumber", value: bioInfo?.mobile }),
          );
          navigate("/", { replace: true });
        })
        .catch((error) => {
          const currentPath = window?.location?.pathname;
          triggerSaveUIException({
            moduleName: getModuleNameFromPath(currentPath),
            pageName: currentPath || "",
            statusCode: 500,
            uiDisplayMessage: `fileName(Feature_Login.tsx) and functionName(initiateBiometricScan) `,
            errorMessage: {
              stk: error.stack || "No stack available",
              msg: error.message || "Unknown error occurred",
            },
          });
        });
    }
  };

  useEffect(() => {
    if (
      loginSlice.screenTag === LoginScreenValues.enterOtpScreenForgotmPIN &&
      csAppSlice.channel === E_ChannelOptions.CSAPP &&
      isMobile
    ) {
      let tempMobile = loginSlice.mobilenumber || mobileNoFromLocalStorage;
      generateOtpFun(LoginScreenValues.enterOtpScreenForgotmPIN, tempMobile);
    }
  }, [loginSlice.screenTag]);

  useEffect(() => {
    if (
      csAppSlice.channel === E_ChannelOptions.SECONDARY_CHANNEL &&
      secondaryChannelSlice.mobileNo
    ) {
      dispatch(updateLoadingState(true));
      generateOtpFun(
        LoginScreenValues.enterOtpScreen,
        secondaryChannelSlice.mobileNo,
      );
    } else {
      dispatch(updateLoadingState(false));
    }
  }, []);

  useEffect(() => {
    /**Reset the master details */
    if (csAppSlice.channel !== E_ChannelOptions.SECONDARY_CHANNEL) {
      dispatch(updateLoadingState(false));
    }
    dispatch(resetLoginData());
    dispatch(clearCustomerAndLobDetailsArr());
  }, []);

  useEffect(() => {
    if (
      (csAppSlice.channel === E_ChannelOptions.CSAPP ||
        csAppSlice.channel === E_ChannelOptions.SECONDARY_CHANNEL) &&
      (deviceType === DEVICE_TYPE.MOBILE ||
        deviceType === DEVICE_TYPE.PWA_MOBILE)
    ) {
      const mobileNo = CsLocalStorageUtils.get("MOBILE");
      const mPinCapturedForMobile = CsLocalStorageUtils.get("MPIN_FOR_MOBILE");
      let bioInfo = null;
      if (
        !isEmpty(mobileNo) &&
        !isEmpty(csAppSlice.NativeDeviceInfo?.deviceName) &&
        !csAppSlice.NativeDeviceInfo?.isFirstInstall
      ) {
        if (csAppSlice.NativeDeviceInfo?.systemName === "Android") {
          bioInfo = csAppSlice.NativeDeviceInfo?.bioInfo;
          dispatch({
            type: REGISTER_BIO_METRIC,
            payload: bioInfo,
          });
        } else {
          bioInfo = JSON.parse(getCookie(REGISTER_BIO_METRIC) as any);
        }

        if (
          !isEmpty(bioInfo) &&
          !doItLater &&
          !isEmpty(bioInfo?.mobile) &&
          bioInfo?.mobile == mobileNo
        ) {
          dispatch({
            type: REGISTER_BIO_METRIC,
            payload: bioInfo,
          });
          const alreadyPrompted = sessionStorage.getItem("BIOMETRIC_PROMPTED");
          if (!alreadyPrompted) {
            sessionStorage.setItem("BIOMETRIC_PROMPTED", "true");
            attemptBiometricLogin(bioInfo);
          }
        }
        /** Mpin Condition */
        if (
          !isEmpty(mPinCapturedForMobile) &&
          mobileNo === mPinCapturedForMobile
        ) {
          console.log("I am here 2");
          dispatch(updateScreenTag(LoginScreenValues.LoginWithMPIN));
          return;
        }
      } else if (
        !isEmpty(mobileNo) &&
        !isEmpty(csAppSlice.NativeDeviceInfo?.deviceName)
      ) {
        console.log("I am here 3");
        if (
          !isEmpty(mPinCapturedForMobile) &&
          mobileNo === mPinCapturedForMobile
        ) {
          console.log("I am here 4");
          dispatch(updateScreenTag(LoginScreenValues.LoginWithMPIN));
          return;
        }
      }
    }
    dispatch(updateScreenTag(LoginScreenValues.enterMobileScreen));
  }, [csAppSlice.NativeDeviceInfo]);

  const generateOtpFun = async (
    screenTag: LoginScreenValues,
    mobileNumber: string = "",
  ) => {
    if (!isEmpty(mobileNumber)) {
      await generateOtpApi({
        mobileNo: "" + mobileNumber,
        otp: "",
        functionality: functionalityCodeForGenerateOtpApi,
        emailRequired: true,
      }).then((res) => {
        if (res.data?.code === 1) {
          dispatch(
            updateLoginData({
              key: "mobilenumber",
              value: mobileNumber,
            }),
          );
          const tempCsUserId = CsLocalStorageUtils.get("CS_USER_ID");
          if (
            loginSlice.screenTag ===
              LoginScreenValues.enterOtpScreenForgotmPIN &&
            tempCsUserId
          ) {
            dispatch(saveCsUserId(tempCsUserId));
          }
          dispatch(updateScreenTag(screenTag));
        } else {
          dispatch(
            setError({
              error: res.data,
              message: "API failed",
            }),
          );
        }
        dispatch(updateLoadingState(false));
      });
    }
  };

  function callComponentByStage(stage: LoginScreenValues) {
    switch (stage) {
      case "LoginWithMPIN":
        return <LoginWithMPIN />;
      case "enterMobileScreen":
        return (
          <MobileNumberEntry
            headerText="Welcome,"
            headerText2=" lets get you started"
            subheaderText=" Enter details to begin"
            onAskConsentValueChange={onAskConsentValueChange}
          />
        );
      case "enterOtpScreen":
        return <EnterOtp headerText={""} onMaximumAttempt={defaultEmptyFun} />;
      case "enterOtpScreenForgotmPIN":
        return (
          <EnterOtp
            headerText={`OTP Verification`}
            onMaximumAttempt={defaultEmptyFun}
          />
        );
      case "enterMobileScreenForgotPin":
        return (
          <MobileNumberEntry
            headerText="Forgot mPIN"
            subheaderText=" Enter mobile number linked to ABC"
          />
        );
      case "setupMPN":
        return <SetupMpn length={4} />;
      case "enterOtpScreenFormPIN":
        return (
          <EnterOtp
            headerText={`mPIN otp verification`}
            onMaximumAttempt={defaultEmptyFun}
          />
        );
      default:
        console.error(`Function ${stage} not found.`);
    }
  }

  const isUserBlocked = CsLocalStorageUtils.get("OTP_BLOCKED_TIME")?.length > 0;
  const login_page_container =
    getMobileOperatingSystem() === "iOS" &&
    !isEmpty(csAppSlice.NativeDeviceInfo?.deviceName)
      ? "login-page-container"
      : "";

  function getClassName(stage: LoginScreenValues) {
    switch (stage) {
      case "LoginWithMPIN":
        return "mainCardlayout";
      case "enterMobileScreen":
        return isAskForConsentVisible
          ? "mainCardForEnterMobileNumberlayout mainCardForEnterMobileNumberlayoutWithConsentOption"
          : "mainCardForEnterMobileNumberlayout";
      case "enterOtpScreen":
        return "mainCardForEnterOTPlayout";
      case "enterMobileScreenForgotPin":
        return "mainCardForEnterMobileForgetMpinlayout";
      default:
        return "mainCardForEnterMobileNumberlayout";
    }
  }

  const logoImageStyle = {
    web: { width: "10.5rem", height: "5rem" },
    mobile: { width: "6.8125rem", height: "3.25rem" },
  };

  const showFooter =
    (loginSlice.screenTag === "enterMobileScreen" ||
      loginSlice.screenTag === "enterOtpScreen") &&
    isEmpty(csAppSlice.NativeDeviceInfo?.deviceName);

  // Add conditional class for OTP screen
  const isOtpScreen = loginSlice.screenTag === LoginScreenValues.enterOtpScreen;
  const loginPageCommonClass = `login-page-common ${
    isOtpScreen ? "otp-screen-active" : ""
  } `;

  return (
    <>
      {width < 900 ? (
        <Box className={login_page_container}>
          <Box className="feature-login">
            <Box
              className={loginPageCommonClass}
              sx={{
                background: animationStage >= 2 ? Colors.red : Colors.bgGray,
              }}
            >
              {/* Expanding Sphere */}
              {animationStage >= 1 && animationStage < 4 && (
                <div className="background-sphere" aria-hidden="true" />
              )}

              {/* Logo */}
              <div
                className={`header-section ${
                  animationStage >= 2 ? "logo-at-top" : "logo-at-center"
                }`}
              >
                <CustomNetworkImage
                  src={cmsLogo || ABCLogo}
                  alt="abc-logo-img"
                  style={logoImageStyle.mobile}
                  shrinkOnAnimate={true}
                />
              </div>
              {/* One Platform Box */}
              {animationStage >= 3 && (
                <div className="one-platform-container">
                  <div className="one-platform-wrapper">
                    <CustomOnePlatformBox />
                  </div>
                </div>
              )}

              {/* Login Card */}
              <Box
                id="dummy-div"
                ref={dummyDivRef}
                sx={{
                  height: dummyDivHeight || "auto",
                  transition: "height 0.3s ease-in-out",
                  minHeight: "16rem",
                }}
              >
                {" "}
              </Box>
              {animationStage >= 4 && (
                <div
                  ref={loginCardRef}
                  className={`${getClassName(loginSlice.screenTag)} ${
                    getMobileOperatingSystem() === iOSType && isMobile
                      ? "iOS-app-only-style"
                      : ""
                  } login-card-absolute`}
                >
                  <Box display="flex" justifyContent="center">
                    {callComponentByStage(loginSlice.screenTag)}
                  </Box>
                </div>
              )}
            </Box>
          </Box>
        </Box>
      ) : (
        <Box className="feature-login adaptive-web">
          <Grid container height="inherit">
            <Grid
              item
              height="inherit"
              xs={4}
              sx={{
                position: "relative",
                overflow: "auto",
                backgroundColor: Colors.red,
              }}
            >
              <Box className="left-box">
                <Box paddingY="4rem">
                  <CustomOnePlatformBox isWeb={true} />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={8} height="inherit">
              <Box className="right-box">
                <Box>
                  {loginSlice.screenTag === "enterMobileScreen" && (
                    <Box className="header-section">
                      <CustomNetworkImage
                        src={cmsLogo || ABCLogo}
                        alt="abc-logo-img"
                        style={logoImageStyle.web}
                      />
                    </Box>
                  )}
                  <Box
                    className={
                      loginSlice.screenTag === "LoginWithMPIN"
                        ? "mainCardlayout"
                        : "mainCardForEnterMobileNumberlayout"
                    }
                  >
                    <Box display="flex" justifyContent="center">
                      {callComponentByStage(loginSlice.screenTag)}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}
      {showFooter && <Footer />}
      <CustomPopup open={isUserBlocked} handleOpen={defaultEmptyFun}>
        <Box padding="1.5rem 1rem">
          <Box
            display="flex"
            justifyContent="center"
            flexDirection={"column"}
            textAlign={"center"}
            alignItems={"center"}
          >
            <CommonGifAnimation
              animationData={ExclamationMarkGif as T_AnimationData}
            />
            <Typography
              marginTop={"2rem"}
              variant="h3"
              color={Colors.textblack}
            >
              You have reached the maximum attempt of tries
            </Typography>
            <Button
              sx={{ width: "100%", marginTop: "2rem" }}
              type="button"
              variant="contained"
              onClick={() => {
                navigate("" + ABCD_SUPERAPP_URL);
              }}
            >
              Go to ABCD homepage
            </Button>
          </Box>
        </Box>
      </CustomPopup>
    </>
  );
};
export default FeatureLogin;
